


import { computed, defineComponent, onMounted, onUnmounted, ref } from "@vue/composition-api";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import {
  ContentFeed,
  ContentFeedSearchResult,
  ContentFeedTypeEnum,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useContentFeed from "@/hooks/useContentFeed";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import providers from "@/lib/api";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { feedFields } from "@/helpers";
import config from "@/config";
import { TabField } from "@/types";
import { EVENTS } from "@/const";
import Announcements from "@/components/Announcements";
import BasePage from "@/components/BasePage";
import ContentFeedList from "@/components/ContentFeedList";
import EmptyContent from "@/components/EmptyContent";
import PageHeader from "@/components/PageHeader";
import StoriesLine from "@/components/StoriesLine";
import SuggestedCreators from "@/components/SuggestedCreators";

export default defineComponent({
  name: "PageFeed",
  components: {
    SuggestedCreators,
    PageHeader,
    Announcements,
    ContentFeedList,
    BasePage,
    EmptyContent,
    StoriesLine,
  },
  setup() {
    const { authUserStrict } = useAuthUser();
    const { contentFeedFollowingSearch } = useContentFeed();
    const { showNotSafeForWork } = useNSFWUserSettings();
    const contentFeedResponse = ref<ContentFeedSearchResult | null>(null);
    const scroll = ref(false);
    const loading = ref(false);
    const subscriptionLevels = ref<SubscriptionLevel[]>([]);
    const paymentPackages = ref<PaymentPackage[]>([]);

    const contentFeedList = computed(() => contentFeedResponse.value?.result ?? []);
    const emptyFeed = computed(() => !loading.value && contentFeedList.value.length === 0);
    const showAnnouncements = computed(() => config.featureAnnouncements);

    const tabs: TabField[] = [
      {
        name: "home",
        text: $t("Home"),
        to: { name: "home" },
      },
      {
        name: "discover",
        text: $t("Discover"),
        to: { name: "discover" },
      },
    ];

    const getFeed = async (page = 0, replace = true, feedIdToFind?: string) => {
      try {
        scroll.value = false;
        const data = await contentFeedFollowingSearch(
          {
            followerId: authUserStrict.value.id,
            types: [ContentFeedTypeEnum.MEDIA, ContentFeedTypeEnum.EVENT],
            page,
            sort: "PUBLISH_TIME,DESC",
            ...(!showNotSafeForWork.value ? { safeForWork: true } : {}),
          },
          feedIdToFind,
          feedFields()
        );
        if (!replace && contentFeedResponse.value) {
          data.result = [...contentFeedResponse.value.result, ...data.result];
        }
        contentFeedResponse.value = data;
      } finally {
        scroll.value = contentFeedResponse.value?.hasNextPage ?? false;
      }
    };

    const onContentFeedUpdate = (contentFeed: ContentFeed) => {
      if (!contentFeedResponse.value) return;
      contentFeedResponse.value = {
        ...contentFeedResponse.value,
        result: contentFeedResponse.value.result.map(item => (item.id === contentFeed.id ? contentFeed : item)),
      };
    };

    const deleteHandler = (feedId: string) => {
      if (!contentFeedResponse.value) return;
      contentFeedResponse.value = {
        ...contentFeedResponse.value,
        result: contentFeedResponse.value.result.filter(item => item.id !== feedId),
      };
    };

    const getNext = async (cb: Function) => {
      try {
        const nextPage = (contentFeedResponse.value?.page ?? 0) + 1;
        await getFeed(nextPage, false);
      } finally {
        cb();
      }
    };

    const updateFeed = async (payload: { userId: string; feedId?: string }) => {
      if (!contentFeedResponse.value || !contentFeedResponse.value.result.length) {
        return;
      }

      contentFeedResponse.value = {
        ...contentFeedResponse.value,
        result: await getPurchasedContentFeeds(
          { authorId: payload.userId, purchasedFeedId: payload.feedId },
          contentFeedResponse.value,
          undefined,
          feedFields()
        ),
      };
    };

    const getData = async () => {
      loading.value = true;
      getFeed().finally(() => (loading.value = false));
      providers.SubscriptionLevelProvider.find({ userIds: [authUserStrict.value.id] }).then(
        r => (subscriptionLevels.value = r)
      );
      providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.FILESET] }).then(
        r => (paymentPackages.value = r)
      );
    };

    getData();

    onMounted(() => {
      emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
    });
    onUnmounted(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
    });

    return {
      loading,
      tabs,
      emptyFeed,
      scroll,
      contentFeedResponse,
      contentFeedList,
      paymentPackages,
      subscriptionLevels,
      showAnnouncements,
      getNext,
      deleteHandler,
      onContentFeedUpdate,
    };
  },
});
